/**
 * Stock Chart Viewer - Drawing Manager
 * Handles chart drawings (Trend lines, horizontal lines) using Lightweight Charts primitives
 * or Canvas overlays.
 */

import { state, saveState } from '../state.js';

export class DrawingManager {
    constructor(chart, candleSeries) {
        this.chart = chart;
        this.series = candleSeries;
        this.drawings = [];
        this.isDrawing = false;

        this.init();
    }

    init() {
        // Restore existing drawings
        this.restoreDrawings();

        // Subscribe to clicks is handled externally in init.js for now, 
        // but ideally should be moved here if we refactor init.js.
        // For now, we expose methods to be called.
    }

    /**
     * Create a horizontal line at a specific price
     */
    createHorizontalLine(price) {
        if (!this.series) return;

        const line = this.series.createPriceLine({
            price: price,
            color: '#2962FF',
            lineWidth: 2,
            lineStyle: 2, // Dashed
            axisLabelVisible: true,
            title: 'H-Line',
        });

        // Store metadata for persistence
        const drawingData = {
            id: Date.now(),
            type: 'horizontal',
            price: price,
            options: {
                color: '#2962FF',
                lineWidth: 2,
                lineStyle: 2,
                axisLabelVisible: true,
                title: 'H-Line'
            }
        };

        // We store the object reference in state.drawings for runtime management
        // But for persistence, we need a separate structure or enrich state.drawings.
        // Current state.js uses logic: state.drawings.push(line). 
        // This 'line' is the IPriceLine object which is not serializable.

        // Let's change state.drawings to store the serializable data, 
        // and keep a separate runtime map for IPriceLine objects.
    }

    save() {
        // In this architecture, we modify state.savedDrawings[symbol] directly
        // and calling saveState() persists it to localStorage.
        saveState();
    }

    /**
     * Add Text Annotation (using Markers)
     */
    addAnnotation(time, text, position = 'aboveBar') {
        if (!this.series) return;

        const marker = {
            time: time,
            position: position,
            color: '#E91E63',
            shape: position === 'aboveBar' ? 'arrowDown' : 'arrowUp',
            text: text,
            id: Date.now()
        };

        // Initialize markers state if needed
        if (!state.currentMarkers) state.currentMarkers = [];
        state.currentMarkers.push(marker);

        // Sort markers by time (required by Lightweight Charts)
        state.currentMarkers.sort((a, b) => a.time - b.time);

        this.series.setMarkers(state.currentMarkers);

        // Update persistence
        // We append to the shared savedDrawings array
        if (state.currentSymbol && state.savedDrawings) {
            if (!state.savedDrawings[state.currentSymbol]) state.savedDrawings[state.currentSymbol] = [];
            state.savedDrawings[state.currentSymbol].push({
                type: 'annotation',
                time: time,
                text: text,
                position: position,
                id: marker.id
            });
            this.save();
        }
    }

    /**
     * Restore drawings and annotations from saved state
     */
    restoreDrawings() {
        if (!state.savedDrawings || !state.currentSymbol) return;

        const symbolDrawings = state.savedDrawings[state.currentSymbol] || [];

        // Reset current markers
        state.currentMarkers = [];

        symbolDrawings.forEach(d => {
            if (d.type === 'horizontal') {
                const line = this.series.createPriceLine({
                    price: d.price,
                    ...d.options
                });
                state.drawings.push(line);
            } else if (d.type === 'annotation') {
                state.currentMarkers.push({
                    time: d.time,
                    position: d.position,
                    color: '#E91E63',
                    shape: d.position === 'aboveBar' ? 'arrowDown' : 'arrowUp',
                    text: d.text,
                    id: d.id || Date.now()
                });
            } else if (d.type === 'fibonacci') {
                // Re-draw Fib
                drawFibonacciLevels(d.highPrice, d.lowPrice);
            }
        });

        // Apply markers if any
        if (state.currentMarkers.length > 0) {
            state.currentMarkers.sort((a, b) => a.time - b.time);
            this.series.setMarkers(state.currentMarkers);
        }
    }
}

// Standalone functions to integrate with existing functional approach

export function addHorizontalLine(price) {
    if (!state.candleSeries) return;

    const line = state.candleSeries.createPriceLine({
        price: price,
        color: '#2962FF',
        lineWidth: 2,
        lineStyle: 2,
        axisLabelVisible: true,
        title: '',
    });

    state.drawings.push(line); // Keep track for clearing

    saveDrawing({
        type: 'horizontal',
        price: price,
        timestamp: Date.now()
    });
}

function saveDrawing(drawingData) {
    if (!state.currentSymbol) return;

    // Initialize container
    if (!state.persistedDrawings) state.persistedDrawings = {};
    if (!state.persistedDrawings[state.currentSymbol]) state.persistedDrawings[state.currentSymbol] = [];

    state.persistedDrawings[state.currentSymbol].push(drawingData);
    state.persistedDrawings[state.currentSymbol].push(drawingData);
    saveState();
}

export function addAnnotation(time, text) {
    if (!state.drawingManager) return;
    state.drawingManager.addAnnotation(time, text);
}

export function restoreDrawingsForSymbol(symbol) {
    if (!state.candleSeries || !state.persistedDrawings || !state.persistedDrawings[symbol]) return;

    // Clear current runtime drawings first (if any leftovers)
    // clearDrawings() should be called before this.

    state.persistedDrawings[symbol].forEach(d => {
        if (d.type === 'horizontal') {
            const line = state.candleSeries.createPriceLine({
                price: d.price,
                color: '#2962FF',
                lineWidth: 2,
                lineStyle: 2,
                axisLabelVisible: true,
                title: '',
            });
            state.drawings.push(line);
        }
    });
}

export function clearAllDrawings() {
    if (state.candleSeries) {
        // Clear lines
        if (state.drawings.length > 0) {
            state.drawings.forEach(line => state.candleSeries.removePriceLine(line));
            state.drawings = [];
        }
        // Clear markers
        state.currentMarkers = [];
        state.candleSeries.setMarkers([]);
    }

    if (state.currentSymbol && state.persistedDrawings) {
        state.persistedDrawings[state.currentSymbol] = [];
        saveState();
    }

    // Also clear from new state structure if different
    if (state.currentSymbol && state.savedDrawings) {
        state.savedDrawings[state.currentSymbol] = [];
        saveState();
    }
}

/**
 * Draw Fibonacci Retracement Levels
 * Based on visible chart high and low prices
 */
export function drawFibonacciLevels(highPrice, lowPrice) {
    if (!state.candleSeries) return;

    const fibLevels = [
        { level: 0, color: '#787B86' },
        { level: 0.236, color: '#F23645' },
        { level: 0.382, color: '#FF9800' },
        { level: 0.5, color: '#2962FF' },
        { level: 0.618, color: '#4CAF50' },
        { level: 0.786, color: '#9C27B0' },
        { level: 1, color: '#787B86' }
    ];

    const range = highPrice - lowPrice;
    const fibLines = [];

    fibLevels.forEach(fib => {
        const price = highPrice - (range * fib.level);
        const line = state.candleSeries.createPriceLine({
            price: price,
            color: fib.color,
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: true,
            title: `${(fib.level * 100).toFixed(1)}%`,
        });
        fibLines.push(line);
        state.drawings.push(line);
    });

    // Save fibonacci as a grouped drawing
    saveDrawing({
        type: 'fibonacci',
        highPrice,
        lowPrice,
        timestamp: Date.now()
    });

    return fibLines;
}

/**
 * Auto-draw Fibonacci based on visible range high/low
 */
export function autoFibonacci() {
    if (!state.stockData || !state.stockData.candleData) return;

    const candles = state.stockData.candleData;
    if (candles.length < 5) return;

    // Find high and low from visible data
    let high = -Infinity;
    let low = Infinity;

    candles.forEach(c => {
        if (c.high > high) high = c.high;
        if (c.low < low) low = c.low;
    });

    if (high !== -Infinity && low !== Infinity) {
        drawFibonacciLevels(high, low);
    }
}

/**
 * Get current drawing mode
 */
export function getDrawingMode() {
    return state.currentDrawingMode || 'horizontal';
}

/**
 * Set current drawing mode
 */
export function setDrawingMode(mode) {
    state.currentDrawingMode = mode;
}
